import { useMemo } from 'react';
import { 
  Box, Typography , TableContainer , Table , TableHead , TableRow , TableCell , TableBody , Paper 
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import DoughnutGraph from 'components/Home/DoughnutGraph';
import VerticalGraph from 'components/VerticalGraph';
import { CASE_SCORE_MAX } from 'components/Analytics/constant';

const DOUGHNUT_GRAPH_COLORS = ['#009970', '#F5F9FF'];

const AVERAGE_SCORE_LEGENDS = [
  { range: "0-30", color: "#E2483C" },
  { range: "31-60", color: "#F5B847" },
  { range: "61-100", color: "#009970" }
]

const CODING_SCORE_LEGENDS = [
  { range: "0-1.2", color: "#E2483C" },
  { range: "1.2-2.33", color: "#F5B847" },
  { range: "2.34-5.0", color: "#009970" }
]

const useStyles = makeStyles((theme) => ({
  leftSection: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      flexShrink: 0
    },
    metricsContainer: {
      display: 'flex',
      gap: '16px',
      width: '100%'
    },
    chartContainer: {
      display: 'flex',
      // The dial and its chart sit side by side as in the design. A 70px gutter
      // pushed the pair past the half-width column on anything under ~1400px,
      // and flex-wrap then dropped them into a stack; 24px keeps them on one
      // row at real viewport widths. Wrapping stays as a last resort so a very
      // narrow window degrades to a stack instead of overflowing.
      gap: '24px',
      padding: '20px',
      backgroundColor: '#FCFAFA',
      borderRadius: '12px',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      minWidth: 0
    }
}))

export const AttemptRateChart = ({
  attemptRate,
  totalStudents = 100,
  isShortlisting = false,
  // Ordered [{ key, label, count }] of students per attempt-rate band. Supplied
  // only where the API can compute it for real; without it the breakdown table
  // is hidden rather than estimated.
  distribution = null,
  title = 'Attempt rate',
}) => {
  const classes = useStyles();

  const submittedCount = attemptRate?.userSubmittedCount || 0;
  const notSubmittedCount = attemptRate?.userNotSubmittedCount || 0;
  const totalCount = submittedCount + notSubmittedCount;

  const tableRows = useMemo(() => {
    if (isShortlisting) {
      return [
        { type: 'Submitted', students: submittedCount },
        { type: 'Not submitted', students: notSubmittedCount }
      ];
    }

    return (distribution || []).map((band) => ({
      type: band.label,
      students: band.count || 0,
    }));
  }, [distribution, submittedCount, notSubmittedCount, isShortlisting]);

  const showTable = isShortlisting || Boolean(distribution?.length);

  const chartData = totalCount > 0 ? [attemptRate?.attemptRate || 0, 100 - (attemptRate?.attemptRate || 0)] : [0, 100];

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" flexDirection="column" gap="12px">
        <Typography variant="h5-medium" color="neutral.clr-700">
          {title}
        </Typography>
        <Box className={classes.chartContainer}>
          <Box className={classes.leftSection}>
            <DoughnutGraph
              rootStyle={{ padding: 0 }}
              color="#0263FF"
              score={Math.round(attemptRate?.attemptRate || 0)}
              data={chartData}
              labels={["Attempt rate", "Remaining"]}
              title={`of ${totalStudents} students`}
              muiltiProgress={true}
              bgColors={DOUGHNUT_GRAPH_COLORS}
              showTooltip={false}
              isPercentage={true}
            />
          </Box>
          {showTable && (
            <AttemptRateTable rows={tableRows} isShortlisting={isShortlisting} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const AttemptRateTable = ({ rows, isShortlisting = false }) => (
  <TableContainer component={Paper} sx={{ flex: '1 1 240px', minWidth: 0 }}>
    <Table>
      <TableHead sx={{ backgroundColor: '#f5f9fc' }}>
        <TableRow>
          <TableCell>
            <Typography variant="body01-bold">
              {isShortlisting ? 'Student type' : 'Attempt rate'}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body01-bold">Students</Typography>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.type}>
            <TableCell>
              <Typography variant="body01-medium">{row.type}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body01-medium">{row.students}</Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ScoreDistributionChart  = ({
  avgDistribution,
  scoreDistribution,
  boxShadow = false,
  interviewVariantType = 'verbal_mock',
  totalStudents = 100,
  // Ordered [{ key, label, count }] from the API. When present it drives both
  // the legend and the bars, so the two cannot fall out of sync the way a
  // client-side legend list can.
  bands = null,
}) => {
  const classes = useStyles();
    const isCodingMock = interviewVariantType === 'coding_mock' || interviewVariantType === 'coding_iw';
    const isCase = interviewVariantType === 'case_mock' || interviewVariantType === 'case_iw';
    const legends = isCodingMock ? CODING_SCORE_LEGENDS : AVERAGE_SCORE_LEGENDS;
    const maxScore = isCase ? CASE_SCORE_MAX : isCodingMock ? 5 : 100;
    // Fractional scales need decimals to stay meaningful; a percentage doesn't.
    const showDecimals = isCodingMock || isCase;
    const average = avgDistribution?.batchAverageScore || 0;

    return (
      <Box sx={{ width: '100%'  }}>
        <Box display="flex" flexDirection="column" gap="12px">
          <Typography variant="h5-medium" color="neutral.clr-700">
            Batch average & score distribution
          </Typography>
          <Box className={classes.chartContainer}>
            <Box className={classes.leftSection}>
              <DoughnutGraph
                rootStyle={{ padding: 0 }}
                color="#009970"
                score={showDecimals ? Number(average).toFixed(2) : Math.round(average)}
                data={[average, Math.max(0, maxScore - average)]}
                labels={["Score", "Remaining"]}
                title={`of ${totalStudents} students`}
                muiltiProgress={true}
                bgColors={DOUGHNUT_GRAPH_COLORS}
                showTooltip={false}
              />
            </Box>
            <VerticalGraph
              bands={bands}
              averageScoreStats={scoreDistribution}
              averageScoreLegends={legends}
              xAsix="Score range"
              yAsix="Student count"
              totalStudents={totalStudents}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  export default ScoreDistributionChart;